/**
 * Settings API Service
 * Profile management, notifications, and properties
 */

import type { HomeownerPropertyType, PropertyStatus } from "@shared/types";
import type {
	ContractorProfile,
	DesignerProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import { apiClient } from "@/lib/api-client";

export {
	createRealtimeAuthToken,
	getNotifications,
	markAllNotificationsRead,
	markNotificationRead,
	type Notification,
	type NotificationsQuery,
} from "./notifications-api.service";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES
// ============================================================================

export interface HomeownerProfile {
	id: string;
	userId: string;
	name: string;
	email?: string;
	city?: string;
	locality?: string;
	pincode?: string | null;
	/** HOMEOWNER_GOALS slugs. */
	goals?: string[];
	profilePictureUrl?: string;
	createdAt: string;
	updatedAt: string;
}

export interface Property {
	id: string;
	homeownerId: string;
	type: HomeownerPropertyType;
	typeOther?: string | null;
	status?: PropertyStatus | null;
	address?: string;
	city?: string;
	locality?: string;
	sizeSqft?: number;
	rooms?: {
		bedrooms?: number;
		bathrooms?: number;
		livingAreas?: number;
	};
}

// ============================================================================
// PROFILE API
// ============================================================================

export async function getHomeownerProfile(): Promise<HomeownerProfile | null> {
	try {
		const response = await apiClient.get(`${API_BASE}/homeowners/me`);
		return response.data.data.profile;
	} catch {
		return null;
	}
}

export async function updateHomeownerProfile(
	data: Partial<HomeownerProfile>,
): Promise<{
	success: boolean;
	profile?: HomeownerProfile;
	error?: string;
}> {
	try {
		const response = await apiClient.patch(`${API_BASE}/homeowners/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update profile",
		};
	}
}

export async function updateDesignerProfile(
	data: Partial<Omit<DesignerProfile, "id" | "userId" | "isVerified">>,
): Promise<{ success: boolean; profile?: DesignerProfile; error?: string }> {
	try {
		const response = await apiClient.patch(`${API_BASE}/designers/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update profile",
		};
	}
}

export async function updateContractorProfile(
	data: Partial<Omit<ContractorProfile, "id" | "userId" | "isVerified">>,
): Promise<{ success: boolean; profile?: ContractorProfile; error?: string }> {
	try {
		const response = await apiClient.patch(`${API_BASE}/contractors/me`, data);
		return { success: true, profile: response.data.data.profile };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update profile",
		};
	}
}

export async function getProperties(): Promise<Property[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/homeowners/me/properties`,
		);
		return response.data.data.properties;
	} catch {
		return [];
	}
}
